import React, { useEffect } from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import Moment from 'moment';
import { ConfigProvider } from 'antd';
import ptBR from 'antd/lib/locale-provider/pt_BR';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { IntlProvider } from 'react-intl';
//import messages from '@Util/messages';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { createIntlInstance } from '@Locales/config/getIntl';

import store from './store';
import { history } from './store';
import theme from './themeMaterial';

import ptCommon from '@Locales/pt/common/pt.json';
import enCommon from '@Locales/en/common/en.json';
import esCommon from '@Locales/es/common/es.json';

import { getCookie, getMvfThemeOverride } from '@Util';


import { RECAPTCHA_KEY } from '@Constants/apiKey';

import 'typeface-roboto';
import 'moment/locale/pt-br';
import 'moment/locale/es';
import 'semantic-ui-css/semantic.css';
import 'react-block-ui/style.css';
import 'loaders.css/loaders.min.css';
import 'react-redux-toastr/lib/css/react-redux-toastr.min.css';

import { unregister } from './registerServiceWorker';
import registerTimeZone from './registerTimeZone';
import App from './App';
import ReactGA from 'react-ga';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

Sentry.init({
  enabled: process.env.NODE_ENV !== 'test',
  dsn: process.env.REACT_APP_SENTRY_TOKEN,
  integrations: [new BrowserTracing()],
  environment: process.env.NODE_ENV,
  tracesSampleRate: 1.0,
});

ReactGA.initialize(
  'UA-163129052-1', //'UA-159145920-1',
  {
    debug: process.env.NODE_ENV !== 'production',
    titleCase: false,
    gaOptions: {
      siteSpeedSampleRate: 100,
    },
  }
);

const momentLocalesMap = {
  'pt': 'pt-br',   
  'pt-BR': 'pt-br',
  'pt-PT': 'pt',  
  'en': 'en',
  'en-US': 'en',
  'en-GB': 'en-gb',
  'es': 'es',
  'es-ES': 'es',
  'es-MX': 'es',
};


const rootElement = document.getElementById('app-root');

const isEmbeddedInMvf = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('mvfEmbedded') === '1'
      || window.self !== window.top
      || window.location.pathname.startsWith('/legacy/');
  } catch (e) {
    return false;
  }
};

if (isEmbeddedInMvf()) {
  document.body.classList.add('usecore-mvf-embedded');
  try {
    const parentHtml = window.parent && window.parent !== window && window.parent.document
      ? window.parent.document.documentElement
      : null;
    const themeOverride = getMvfThemeOverride();
    const savedTheme = window.localStorage && window.localStorage.getItem('theme');
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const isDark = themeOverride
      ? themeOverride === 'dark'
      : parentHtml
      ? parentHtml.classList.contains('dark')
      : savedTheme === 'dark' || (savedTheme !== 'light' && prefersDark);

    document.body.classList.toggle('usecore-mvf-dark', Boolean(isDark));
  } catch (e) {
    // O App sincroniza o tema novamente depois de montar.
  }
}

const auth = JSON.parse(getCookie('auth') || '{}');
const defaultLanguage = auth.languageCode;
const locale = defaultLanguage || navigator.language || navigator.userLanguage;
const language = locale.split('-')[0]; // "pt"

const momentLocale = momentLocalesMap[locale] || momentLocalesMap[language] || 'en';



const messages = {
  pt: {
    ...ptCommon,
  },
  en: {
    ...enCommon,
  },
  es: {
    ...esCommon,
  },
};

createIntlInstance(language, messages[language]);

class Root extends React.Component {
  constructor(props) {
    super(props);
    
    const auth = JSON.parse(getCookie('auth') || '{}');
    const defaultLanguage = auth.languageCode;
    const locale = defaultLanguage || navigator.language || navigator.userLanguage;
    const language = locale.split('-')[0];
    
    this.state = {
      language,
      locale,
      momentLocale: momentLocalesMap[locale] || momentLocalesMap[language] || 'en'
    };
  }

  componentDidMount() {
    this.checkLanguageChange = setInterval(() => {
      const auth = JSON.parse(getCookie('auth') || '{}');
      const defaultLanguage = auth.languageCode;
      if (defaultLanguage && defaultLanguage !== this.state.locale) {
        const locale = defaultLanguage;
        const language = locale.split('-')[0];
        const momentLocale = momentLocalesMap[locale] || momentLocalesMap[language] || 'en';
        
        this.setState({ language, locale, momentLocale });
        createIntlInstance(language, messages[language]);
        Moment.locale(momentLocale);
      }
    }, 100);
  }

  componentWillUnmount() {
    if (this.checkLanguageChange) {
      clearInterval(this.checkLanguageChange);
    }
  }

  render() {
    const { language, momentLocale } = this.state;
    
    return (
      <Provider store={store}>
        <IntlProvider locale={language} messages={messages[language]}>
          <ConnectedRouter history={history}>
            <MuiThemeProvider theme={theme}>
              <ConfigProvider locale={ptBR}>
                <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_KEY}>
                  <App history={history} momentLocale={momentLocale}/>
                </GoogleReCaptchaProvider>
              </ConfigProvider>
            </MuiThemeProvider>
          </ConnectedRouter>
        </IntlProvider>
      </Provider>
    );
  }
}

render(<Root />, rootElement);
unregister();
registerTimeZone();

if (process.env.NODE_ENV !== 'production') {
  if (module.hot) {
    module.hot.accept('./App', () => render(<Root />, rootElement));
  }
}
